"use client";

import { ReactNode, useEffect, useState } from "react";
import { cn } from "~/lib/utils";

import {
    Carousel as CarouselWrap,
    CarouselApi,
    CarouselProps,
} from "~/components/ui/carousel";

import {
    DotButton,
    useDotButton,
} from "~/componentsND/carousel/CarouselDotButton";

import usePrevNextButtons from "~/componentsND/carousel/usePrevNextButtons";

import { ArrowRightIcon } from "@phosphor-icons/react/ssr";
import ArrowRightPurpleIcon from "@/icons/arrows/arrow-right-purple.svg";

type CarouselVariant = "default" | "compact" | "responsive";
type CompactPaginationAlign = "left" | "center" | "right" | "right12" | "right9";

type Props = CarouselProps & {
    children: ReactNode;
    className?: string;
    arrows?: boolean;
    dots?: boolean;
    dotCount?: number;
    variant?: CarouselVariant;
    compactPaginationAlign?: CompactPaginationAlign;
    compactPaginationBg?: string;
    compactPaginationDotColor?: string;
    compactPaginationClassName?: string;
    onSelect?: (emblaApi: CarouselApi) => void;
    onDotClick?: (index: number) => void;
    draggable?: boolean;
    controlsClassName?: string;
};

const paginationAlignClasses: Record<CompactPaginationAlign, string> = {
    left: "left-4 translate-x-0",
    center: "left-1/2 -translate-x-1/2",
    right: "right-4 translate-x-0",
    right12: "right-12 translate-x-0",
    right9: "right-9 md:right-4 translate-x-0",
};

export const carouselDefaultArrowClasses = cn(
    "inline-flex h-10 w-10 items-center justify-center rounded-full text-[#17409C] transition-all duration-200",
    "hover:bg-primary100",
    "active:bg-[#D7E1FF] active:border active:border-[rgba(0,0,0,0.05)]",
    "active:shadow-[inset_0_-1px_1px_0_rgba(0,0,0,0.05),inset_0_1px_2px_-1px_rgba(0,0,0,0.03)]",
    "disabled:pointer-events-none disabled:opacity-40"
);

export const carouselCompactArrowClasses = cn(
    "inline-flex h-8 w-8 items-center justify-center rounded-full bg-primary300 p-2 pointer-events-auto transition-all duration-200",
    "hover:bg-primary400",
    "active:bg-primary500",
    "active:shadow-[inset_0_-1px_1px_0_rgba(0,0,0,0.05),inset_0_1px_2px_-1px_rgba(0,0,0,0.03)]",
    "disabled:pointer-events-none disabled:opacity-40"
);

export const Carousel = ({
    children,
    className,
    arrows = true,
    dots = true,
    dotCount,
    variant = "default",
    compactPaginationAlign = "center",
    compactPaginationBg = "bg-gray100",
    compactPaginationDotColor = "bg-gray200",
    compactPaginationClassName,
    onSelect,
    onDotClick,
    setApi: externalSetApi,
    draggable = true,
    controlsClassName,
    ...props
}: Props) => {
    const [emblaApi, setApi] = useState<CarouselApi>();
    const [isScrollable, setIsScrollable] = useState(true);

    /* -----------------------------
       Embla helpers
    ----------------------------- */

    const {
        prevBtnDisabled,
        nextBtnDisabled,
        onPrevButtonClick,
        onNextButtonClick,
    } = usePrevNextButtons(emblaApi);

    const { selectedIndex, scrollSnaps, onDotButtonClick } =
        useDotButton(emblaApi);

    /* -----------------------------
       Detect if carousel is needed
    ----------------------------- */

    useEffect(() => {
        if (!emblaApi) return;

        const updateScrollableState = () => {
            const canScroll =
                emblaApi.canScrollNext() || emblaApi.canScrollPrev();

            setIsScrollable(canScroll);
        };

        updateScrollableState();

        emblaApi.on("reInit", updateScrollableState);
        emblaApi.on("resize", updateScrollableState);

        return () => {
            emblaApi.off("reInit", updateScrollableState);
            emblaApi.off("resize", updateScrollableState);
        };
    }, [emblaApi]);

    /* -----------------------------
       Forward API to external consumer
    ----------------------------- */

    useEffect(() => {
        if (emblaApi && externalSetApi) {
            externalSetApi(emblaApi);
        }
    }, [emblaApi, externalSetApi]);

    /* -----------------------------
       External onSelect hook
    ----------------------------- */

    useEffect(() => {
        if (!emblaApi || !onSelect) return;

        const handleSelect = () => onSelect(emblaApi);

        emblaApi.on("select", handleSelect);

        return () => {
            emblaApi.off("select", handleSelect);
        };
    }, [emblaApi, onSelect]);

    /* -----------------------------
       Dots logic
    ----------------------------- */

    const dotsArray = dotCount
        ? Array.from({ length: dotCount })
        : scrollSnaps;

    const activeDotIndex = dotCount
        ? selectedIndex % dotCount
        : selectedIndex;

    /* -----------------------------
       UI render helpers
    ----------------------------- */

    const renderNavigationButton = (
        direction: "prev" | "next",
        isCompact: boolean
    ) => {
        const onClick =
            direction === "prev"
                ? onPrevButtonClick
                : onNextButtonClick;

        const disabled =
            direction === "prev"
                ? prevBtnDisabled
                : nextBtnDisabled;

        return (
            <button
                onClick={onClick}
                disabled={disabled}
                className={
                    isCompact
                        ? carouselCompactArrowClasses
                        : carouselDefaultArrowClasses
                }
            >
                {isCompact ? (
                    <ArrowRightIcon
                        size={24}
                        color="white"
                        className={cn(direction === "prev" && "rotate-180")}
                    />
                ) : (
                    <ArrowRightPurpleIcon
                        size={40}
                        className={cn(
                            direction === "prev" && "rotate-180"
                        )}
                    />
                )}
            </button>
        );
    };

    const renderDots = (isCompact: boolean) =>
        dotsArray.map((_, index) => {
            const isActive = index === activeDotIndex;

            return (
                <DotButton
                    key={index}
                    onClick={() => {
                        onDotButtonClick(index);
                        onDotClick?.(index);
                    }}
                    className={cn(
                        "embla__dot h-[6px] rounded transition-all duration-300",
                        {
                        // active
                            "bg-primary300 w-[16px]": isActive && isCompact,
                            "bg-primary300 w-[6px]": isActive && !isCompact,

                            // inactive
                            [compactPaginationDotColor]:
                            isCompact && !isActive,
                            "bg-[#9AB1F5]":
                            !isCompact && !isActive,

                            // width
                            "w-[6px]": !isActive,
                        }
                    )}
                />
            );
        });

    /* -----------------------------
       Controls render
    ----------------------------- */

    const shouldRenderControls =
        isScrollable && (arrows || dots);

    const renderDefaultControls = () => (
        <div className="flex items-center justify-center gap-6 mt-4 md:mt-8">
            {arrows && renderNavigationButton("prev", false)}
            {dots && <div className="flex gap-2">{renderDots(false)}</div>}
            {arrows && renderNavigationButton("next", false)}
        </div>
    );

    const renderCompactControls = () => (
        <>
            {arrows && (
                <div className="absolute inset-0 flex justify-between items-center px-3 pointer-events-none opacity-0 md:group-hover/carousel:opacity-100 transition-opacity duration-300">
                    {renderNavigationButton("prev", true)}
                    {renderNavigationButton("next", true)}
                </div>
            )}

            {dots && (
                <div
                    className={cn(
                        "absolute bottom-0 py-4",
                        paginationAlignClasses[compactPaginationAlign],
                        compactPaginationClassName
                    )}
                >
                    <div
                        className={cn(
                            "inline-flex gap-1 p-2 rounded-full",
                            compactPaginationBg
                        )}
                    >
                        {renderDots(true)}
                    </div>
                </div>
            )}
        </>
    );

    const renderControls = () => {
        if (!shouldRenderControls) return null;

        if (variant === "default") return renderDefaultControls();
        if (variant === "compact") return renderCompactControls();

        // responsive: mobile = compact, md+ = default
        return (
            <>
                <div className="md:hidden">
                    {renderCompactControls()}
                </div>
                <div className="hidden md:block">
                    {renderDefaultControls()}
                </div>
            </>
        );
    };

    /* -----------------------------
       Render
    ----------------------------- */

    return (
        <CarouselWrap
            {...props}
            setApi={setApi}
            opts={{
                ...(props.opts ?? {}),
                watchDrag: !draggable
                    ? false
                    : props.opts?.watchDrag ?? isScrollable,
            }}
            className={cn("relative group/carousel", className)}
        >
            {children}
            {controlsClassName ? (
                <div className={controlsClassName}>{renderControls()}</div>
            ) : renderControls()}
        </CarouselWrap>
    );
};

export default Carousel;
